import React from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';

import { store } from '../store';
import { Config } from '../config';
import AppContainer from './AppContainer';
import { queryClient } from './queryClient';
import { colorScheme, zIndex } from '../theme';
import { createGlobalStyle } from 'styled-components';

const appHeight = () => {
  const doc = document.documentElement;
  doc.style.setProperty('--app-height', `${window.innerHeight}px`);
};

const render = () => {
  return <div />;
};

window.addEventListener('resize', appHeight);
appHeight();

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CSSGlobalStyles />
      <Provider store={store}>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          limit={4}
        />
        <Wrapper apiKey={Config.GoogleApiKey} render={render}>
          <AppContainer className="row centered-form center-block" />
        </Wrapper>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;

const CSSGlobalStyles = createGlobalStyle`
body {
  background-color: ${colorScheme.globalBackground};
  color: ${colorScheme.text.base};
}

.flex-fill {
  flex: 1 1 auto;
  padding: 0px;
  background-color: ${colorScheme.globalBackground};
}

.hdn-event-modal {
  background-color: ${colorScheme.globalBackground};
}

.dropdown-menu, .popover {
  background: ${colorScheme.card.background};
  border-color: ${colorScheme.card.border};
  color: ${colorScheme.text.base};
}

.dropdown-item:focus, .dropdown-item:hover {
  background-color: ${colorScheme.semitransparent};
  color: ${colorScheme.text.base};
}

.dropdown-divider {
  border-color: ${colorScheme.divider};
}

.dropdown-item {
  color: ${colorScheme.text.base};
}

.form-control, .form-control:focus, .form-control:hover, .form-control:disabled {
  border-color: ${colorScheme.input.border};
  background-color: ${colorScheme.input.background};
  color: ${colorScheme.input.text};
}

.form-control:disabled {
  opacity: 0.5;
}

html body {
  .react-datepicker,
  .react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input {
    background-color: ${colorScheme.card.background};
    border-color: ${colorScheme.card.border};
    color: ${colorScheme.text.base};
  }

  .react-datepicker__header {
    background: transparent;
  }

  .react-datepicker__current-month, 
  .react-datepicker-time__header, 
  .react-datepicker-year-header,
  .react-datepicker__day-name, 
  .react-datepicker__day, 
  .react-datepicker__time-name,
  .react-datepicker__input-time-container .react-datepicker-time__caption {
    color: ${colorScheme.text.base};
  }

  .react-datepicker__day:hover, 
  .react-datepicker__month-text:hover, 
  .react-datepicker__quarter-text:hover, 
  .react-datepicker__year-text:hover {
    background-color: ${colorScheme.focus};
    color: ${colorScheme.text.base};
  }

  /* FIXME we're forcing light mode for now */
  /*
  @media (prefers-color-scheme: dark) {
    .react-datepicker__navigation {
      filter: invert(1); 
    }
  }
    */

  .react-datepicker__month-container:nth-of-type(2n+1) {
    background-color: ${colorScheme.card.border} !important;
  }
}

.hdn-input, 
input[type="text"], 
input[type="password"], 
input[type="email"], 
input[type="number"], 
input[type="search"], 
input[type="date"], 
textarea {
  background: ${colorScheme.input.background};
  border: 1px solid ${colorScheme.input.border};
  color: ${colorScheme.input.text};

  &:placeholder {
    color: ${colorScheme.text.base};
  }

  &:hover {
    &:not(:disabled) {
      background: ${colorScheme.input.background};
      border: 1px solid ${colorScheme.text.base};
    } 
  }

  &:focus {
    border-color: ${colorScheme.focus};
  }

  &:focus + .hdn-input__label,
  &:not(:placeholder-shown) + .hdn-input__label {
    transform: scale(0.75) translateY(calc(40px * -1));
    background: ${colorScheme.input.background};
  }

  &__label {
    color: ${colorScheme.input.text};
  }
}

/* FIXME move all of this to respectful input components */
select, select.custom-select {
  background-color: ${colorScheme.input.background};
  border: 1px solid ${colorScheme.input.border};
  color: ${colorScheme.input.text};

  &:placeholder {
    color: ${colorScheme.input.text};
  }
}

.ReactModal__Overlay.ReactModal__Overlay--after-open {
    background-color: rgba(128, 128, 128, 0.75) !important;
    z-index: ${zIndex.dialog};
    position: relative;
    top: 1px;
}

.hdn-modal {
  border-radius: 8px;
  background: ${colorScheme.card.background};
  box-shadow: 0px 0px 12px 6px ${colorScheme.semitransparent};
  padding: 24px 32px;
  &__header {
    @include typography.text--heading-1;
  margin-bottom: 40px;
}
  &__footer {
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
}
}
`;
